import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  CircleCheck,
  Clock,
  Mail,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Notification, NotificationType } from '../../../shared/types';
import { api, ApiError } from '../../lib/api';
import { qk } from '../../lib/query';
import { formatRelative } from '../../lib/format';
import { useWorkspaceStore } from '../../stores/workspace';
import { Button, Spinner } from '../ui';
import { cn } from '../../lib/utils';

const ICONS: Record<NotificationType, LucideIcon> = {
  publish_success: CircleCheck,
  publish_failure: AlertTriangle,
  post_scheduled: Clock,
  token_expiring: AlertTriangle,
  token_expired: AlertTriangle,
  invite_sent: Mail,
  invite_accepted: UserPlus,
  member_joined: UserPlus,
  style_analyzed: Sparkles,
  system: Bell,
};

const TONES: Partial<Record<NotificationType, string>> = {
  publish_success: 'text-success',
  publish_failure: 'text-danger',
  token_expiring: 'text-warning',
  token_expired: 'text-danger',
  style_analyzed: 'text-accent',
};

/** Poll interval for the unread count — quiet enough to be invisible. */
const POLL_MS = 60_000;

export function NotificationBell({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const notifications = useQuery({
    queryKey: qk.notifications(workspaceId, false),
    queryFn: () => api.notifications.list({ limit: 12 }),
    enabled: Boolean(workspaceId),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    // A deployment whose notifications endpoint is unavailable should degrade to
    // an empty bell, never to a broken shell.
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => api.notifications.markRead(ids ? { ids } : { all: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notifications(workspaceId, false) });
    },
  });

  const items = notifications.data?.notifications ?? [];
  const unread = notifications.data?.unreadCount ?? 0;
  const unavailable = notifications.error instanceof ApiError;

  const openNotification = (notification: Notification) => {
    setOpen(false);
    if (!notification.readAt) markRead.mutate([notification.id]);
    if (notification.link) navigate(notification.link);
  };

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        className={cn(
          'relative inline-flex size-9 items-center justify-center rounded-md text-muted transition-colors',
          'hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          open && 'bg-elevated text-fg',
        )}
      >
        <Bell className="size-4" aria-hidden />
        {unread > 0 && (
          <span className="pap-tnum absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-accent-fg ring-2 ring-surface">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Click-away layer; the panel itself stops propagation. */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
            <motion.div
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
              className="pap-pop absolute right-0 top-full z-50 mt-2 w-[21rem] max-w-[calc(100vw-2rem)] overflow-hidden"
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <p className="text-sm font-semibold text-fg">Notifications</p>
                {unread > 0 && (
                  <Button
                    variant="link"
                    size="xs"
                    leftIcon={<CheckCheck className="size-3.5" />}
                    onClick={() => markRead.mutate(undefined)}
                  >
                    Mark all read
                  </Button>
                )}
              </div>

              <div className="max-h-[22rem] overflow-y-auto">
                {notifications.isLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner label="Loading" />
                  </div>
                ) : unavailable ? (
                  <p className="px-4 py-8 text-center text-xs text-subtle">
                    Notifications are unavailable right now.
                  </p>
                ) : items.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm font-medium text-fg">You are all caught up</p>
                    <p className="mt-1 text-xs text-muted">
                      Publishing results and invites land here.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {items.map((notification) => {
                      const Icon = ICONS[notification.type] ?? Bell;
                      return (
                        <li key={notification.id}>
                          <button
                            type="button"
                            onClick={() => openNotification(notification)}
                            className={cn(
                              'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface',
                              !notification.readAt && 'bg-accent-soft/40',
                            )}
                          >
                            <Icon
                              className={cn(
                                'mt-0.5 size-4 shrink-0',
                                TONES[notification.type] ?? 'text-subtle',
                              )}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span className="truncate text-sm font-medium text-fg">
                                  {notification.title}
                                </span>
                                {!notification.readAt && (
                                  <span className="size-1.5 shrink-0 rounded-full bg-accent" aria-hidden />
                                )}
                              </span>
                              {notification.body && (
                                <span className="pap-clamp-2 mt-0.5 block text-xs leading-relaxed text-muted">
                                  {notification.body}
                                </span>
                              )}
                              <span className="mt-1 block text-[11px] text-subtle">
                                {formatRelative(notification.createdAt)}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
